import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  LinearProgress,
  Alert,
  IconButton,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { useAuth, redirectToLogin } from "../common/AuthContext.jsx";
import { gql, useLazyQuery } from "@apollo/client";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  extractAndStoreCsrfToken,
  getCsrfTokenSync,
  ensureCsrfToken,
  getCsrfTokenKeyByEnv,
} from "../../utils/csrf.js";
import EscalationInfo from '../common/EscalationInfo';
import { GRAPHQL_ENDPOINTS } from '../../config/api';
import { BUSINESS_CONSTANTS } from '../../config/businessConstants';
import { extractApiErrorMessage } from '../../utils/apiErrorMessage.js';
import { getAuthToken } from '../../utils/auth.js';
import { apiFetch } from '../../utils/api';


// Add the missing GraphQL query for getGrievanceTransactionAndTicketDetails
export const GET_GRIEVANCE_TRANSACTION_AND_TICKET_DETAILS = gql`
  query getGrievanceTransactionAndTicketDetails($input: GrievanceTransactionAndTicketDetailsFilter) {
    getGrievanceTransactionAndTicketDetails(input: $input) {
      status
      message
      data {
        statusKey
        statusMsg
        ticketDetailsCurrentPage
        pageSize
        hasNext
        page
        currentPage
        totalCount
        ticketDetailsTotalPages
        transactions {
          id
          date
          mode
          email
          amount
          refundAmount
          status
          merchantId
        }
        ticketDetails {
          issueId
          id
          transactionId
          description
          status
          createdOn
        }
        customer {
          id
          name
          mobileNo
          email
        }
      }
    }
  }
`;

// Simple stage definitions for workflow UI
const TICKET_STAGES = {
  OPEN: {
    id: 3,
    name: "Open",
    description: "Ticket has been created and is awaiting initial review",
    color: "#FFBF00",
    backgroundColor: "#FFBF0033"
  },
  IN_PROGRESS: {
    id: 4,
    name: "In Progress", 
    description: "Ticket is being actively worked on by support team",
    color: "#0065FF",
    backgroundColor: "#0065FF33"
  },
  PROCESSING: {
    id: 2,
    name: "Processing",
    description: "Ticket is being processed and investigated", 
    color: "#00A3BF",
    backgroundColor: "#E6FCFF"
  },
  RESOLVED: {
    id: 1,
    name: "Resolved",
    description: "Ticket has been resolved and closed",
    color: "#00875A", 
    backgroundColor: "#36B37E33"
  }
};

const CheckYourQuery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const csrfTokenKey = getCsrfTokenKeyByEnv();
  const { isAuthenticated, setIsAuthenticated, isLoading, clearAuthState } = useAuth();
  const otpRefs = useRef([]);
  const isMobile = useMediaQuery("(max-width:600px)");

  // Constants
  const OTP_LENGTH = 6;

  const [openIndex, setOpenIndex] = useState(null);
  const [emailInfo, setEmailInfo] = useState({ email: "", submitted: false });
  const [timer, setTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [paymentType, setPaymentType] = useState("mobile");
  const [inputField, setInputField] = useState("");
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
  const [otpState, setOtpState] = useState(false);
  const [otpVerified, setOtpVerified] = useState(null);
  const [otpVerificationStatus, setOtpVerificationStatus] = useState(null); // 'success' or 'failed'
  const [escalate, setEscalate] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [queries, setQueries] = useState(false);
  const [ticketQueries, setTicketQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [escalateTicketDetails, setEscalateTicketDetails] = useState(null);
  const [escalateLoading, setEscalateLoading] = useState(false);
  const [escalationReason, setEscalationReason] = useState("");
  const [escalationComment, setEscalationComment] = useState("");
  const [escalationSubmitting, setEscalationSubmitting] = useState(false);
  const [escalationSuccessMessage, setEscalationSuccessMessage] = useState("");
  const itemsPerPage = BUSINESS_CONSTANTS.PAGINATION.DEFAULT_ITEMS_PER_PAGE;
  const [showAllCards, setShowAllCards] = useState(false);
  const [otpKey, setOtpKey] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [otpRequestError, setOtpRequestError] = useState("");
  const [otpError, setOtpError] = useState("");
  const changeClickedRef = useRef(false);
  const changingEmailRef = useRef(false);
  const otpContainerRef = useRef(null);
  const [otpIptDimension, setOtpIptDimension] = useState({ width: 0, height: 0 });

  // Add state for ticket pagination
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);
  const [ticketTotalCount, setTicketTotalCount] = useState(0);

  const [getTicketDetails] = useLazyQuery(GET_GRIEVANCE_TRANSACTION_AND_TICKET_DETAILS);
  const sessionRestoredRef = useRef(false);

  // After refresh, restore ticket list when a grievance session token already exists
  useEffect(() => {
    if (isLoading) return;

    // If navigated here with bypassAuth, skip session restore and show fresh email input
    if (location.state?.bypassAuth) {
      sessionRestoredRef.current = false;
      return;
    }

    const token = getAuthToken();
    if (!token || !isAuthenticated) {
      sessionRestoredRef.current = false;
      return;
    }
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;

    setOtpState(false);
    setOtpVerificationStatus('success');
    setOtpVerified(true);
    fetchTicketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per auth+token; fetchTicketDetails is stable enough for restore
  }, [isLoading, isAuthenticated]);

  // API call function
  const fetchTicketDetails = async (pageOverride, paymentId = '') => {
    setLoading(true);
    setError(null);

    // If no access token is present yet, exit silently. AuthContext attempts
    // a refresh in the background; once isAuthenticated flips true the
    // session-restore effect will call fetchTicketDetails again. Showing an
    // "Authentication required" error here caused the page to briefly render
    // "no tickets found" right after a refresh while the token was being
    // re-issued.
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      setLoading(false);
      return;
    }

    // Calculate endDate as today, startDate as 59 days before today
    const today = new Date();
    const endDate = today.toISOString().slice(0, 10) + " 23:59:59"; // YYYY-MM-DD HH:MM:SS
    const startDateObj = new Date(today);
    startDateObj.setDate(startDateObj.getDate() - BUSINESS_CONSTANTS.DATE_RANGES.DEFAULT_DAYS_BACK); // 59 days back
    const startDate = startDateObj.toISOString().slice(0, 10) + " 00:00:00"; // YYYY-MM-DD HH:MM:SS

    try {
      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          [csrfTokenKey]: getCsrfTokenSync()
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({
          query: `query getGrievanceTransactionAndTicketDetails($input: GrievanceTransactionAndTicketDetailsFilter) {
            getGrievanceTransactionAndTicketDetails(input: $input) {
              status
              message
              data {
                statusKey
                statusMsg
                ticketDetailsCurrentPage
                pageSize
                hasNext
                page
                currentPage
                totalCount
                ticketDetailsTotalPages
                transactions {
                  id
                  date
                  mode
                  email
                  amount
                  refundAmount
                  status
                  merchantId
                }
                ticketDetails {
                  issueId
                  id
                  transactionId
                  description
                  status
                  createdOn
                }
                customer {
                  id
                  name
                  mobileNo
                  email
                }
              }
            }
          }`,
          variables: {
            input: {
              startDate: startDate,
              endDate: endDate,
              page: pageOverride ?? ticketPage,
              pageSize: itemsPerPage,
              ...(paymentId && String(paymentId).trim()
                ? { paymentId: String(paymentId).trim() }
                : {}),
            }
          }
        })
      });
      const result = await response.json();
      if (result.data?.getGrievanceTransactionAndTicketDetails?.status === "SUCCESS" || 
          result.data?.getGrievanceTransactionAndTicketDetails?.status === "OK") {
        const apiData = result.data.getGrievanceTransactionAndTicketDetails.data;
        const ticketDetails = apiData?.ticketDetails || [];
        const mappedQueries = mapApiDataToQueries(ticketDetails, apiData);
        setTicketQueries(mappedQueries);
        const responsePage = apiData.ticketDetailsCurrentPage ?? apiData.currentPage;
        if (typeof responsePage === 'number' && responsePage > 0) {
          setTicketPage(responsePage);
        }
        setTicketTotalPages(apiData.ticketDetailsTotalPages || 1);
        setTicketTotalCount(apiData.totalCount || 0);
        const customerEmail = apiData?.customer?.email?.trim();
        if (customerEmail) {
          const normalized = customerEmail.toLowerCase();
          setEmailInfo((prev) =>
            prev.email ? prev : { email: normalized, submitted: true }
          );
        }
        // Show success toast with statusMsg
        const statusMsg = apiData?.statusMsg || "Ticket details fetched successfully";
      } else {
        const errorMsg = result.data?.getGrievanceTransactionAndTicketDetails?.message || "Failed to fetch ticket details";
        setError(errorMsg);
        setTicketQueries([]);
      }
    } catch (err) {
      console.error("Error fetching ticket details:", err);
      
      // Check if it's an authentication error
      if (err.message && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
        setError("Session expired. Please log in again.");
        // Don't clear tokens immediately, let AuthContext handle session validation
        // This prevents logout on temporary network issues
      } else {
        const errorMsg = "Failed to fetch ticket details. Please try again.";
        setError(errorMsg);
      }
      setTicketQueries([]);
    } finally {
      setLoading(false);
    }
  };

  const mapApiDataToQueries = (ticketDetails, apiData) => {
    if (!ticketDetails || ticketDetails.length === 0) return [];
    
    return ticketDetails.map(ticket => ({
      id: ticket.id,
        transactionId: ticket.transactionId,
      // Map and parse createdOn
      createdOn: ticket.createdOn || null,
      description: ticket.description || "No description available",
      status: ticket.status || "Unknown",
      currentEscalationLevel: 1, // Default escalation level
      lastUpdated: "N/A", // API doesn't provide last updated date
      issueId: ticket.issueId
    }));
  };

  // API call function for ticket details when escalating
  const escalateTicket = async (ticketId, reason, comment) => {
    setEscalationSubmitting(true);
    setError(null);

    try {
      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
          [csrfTokenKey]: getCsrfTokenSync()
        },
        credentials: 'include', // Include cookies in the request
        body: JSON.stringify({
          query: `mutation escalateTicket($input: EscalateTicketFilter!) {
            escalateTicket(input: $input) {
              status
              message
              data {
                id
                ticketId
                status
                statusMsg
                previousLevel
                currentLevel
              }
            }
          }`,
          variables: {
            input: {
              ticketId: ticketId.toString(),
              reason: reason,
              comment: comment
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.data?.escalateTicket?.status === "OK" || result.data?.escalateTicket?.status === "SUCCESS") {
        const statusMsg = result.data.escalateTicket.data?.statusMsg || result.data.escalateTicket.message || "Your response has been submitted.";
        setEscalationSuccessMessage(statusMsg);
        setEscalationReason("");
        setEscalationComment("");
        navigate("/");
        // Don't reset escalate state here - let the useEffect handle navigation
      } else {
        const errorMsg = result.data?.escalateTicket?.message || "Failed to escalate ticket";
        setError(errorMsg);
      }
    } catch (err) {
      console.error("Error escalating ticket:", err);
      setError("Failed to escalate ticket. Please try again.");
    } finally {
      setEscalationSubmitting(false);
    }
  };

  useEffect(() => {
    let countdown;
    if (otpState && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
      clearInterval(countdown);
    }
    return () => clearInterval(countdown);
  }, [otpState, timer]);

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        // Reset escalate state before navigation
        setEscalate(false);
        setEscalateTicketDetails(null);
        navigate("/");
      }, BUSINESS_CONSTANTS.TIMEOUTS.SUCCESS_MESSAGE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, navigate]);

  useEffect(() => {
    setQueries(otpVerified === true && !escalate);
  }, [otpVerified, escalate]);

  // When ticketPage changes, fetch new page
  useEffect(() => {
    if (emailInfo.email && otpVerified) {
      fetchTicketDetails();
    }
    // eslint-disable-next-line
  }, [ticketPage]);

  // Dynamic OTP box sizing — runs when OTP screen becomes visible
  useEffect(() => {
    if (!otpState) return;
    const updateWidth = () => {
      if (otpContainerRef.current) {
        const width = otpContainerRef.current.clientWidth / 6 - 8;
        setOtpIptDimension({ width, height: width * 1.2 });
      }
    };
    const t = setTimeout(updateWidth, 0);
    window.addEventListener('resize', updateWidth);
    return () => { clearTimeout(t); window.removeEventListener('resize', updateWidth); };
  }, [otpState]);

  const calculateGridWidth = () => {
    if (!queries) {
      return { xs: 10, sm: 10, md: 6, lg: 6, xl: 4 };
    }

    const queryCount = ticketQueries.length;
    const maxColumns = isMobile ? 1 : 3;
    const columnsNeeded = Math.min(
      Math.ceil(queryCount / (isMobile ? 1 : itemsPerPage / maxColumns)),
      maxColumns
    );

    const baseWidths = { xs: 8, sm: 8, md: 6, lg: 6, xl: 6 };
    const maxWidths = { xs: 10, sm: 10, md: 10, lg: 10, xl: 10 };

    const widthFactor = columnsNeeded / maxColumns;
    return {
      xs: Math.min(
        baseWidths.xs + (maxWidths.xs - baseWidths.xs) * widthFactor,
        maxWidths.xs
      ),
      sm: Math.min(
        baseWidths.sm + (maxWidths.sm - baseWidths.sm) * widthFactor,
        maxWidths.sm
      ),
      md: Math.min(
        baseWidths.md + (maxWidths.md - baseWidths.md) * widthFactor,
        maxWidths.md
      ),
      lg: Math.min(
        baseWidths.lg + (maxWidths.lg - baseWidths.lg) * widthFactor,
        maxWidths.lg
      ),
      xl: Math.min(
        baseWidths.xl + (maxWidths.xl - baseWidths.xl) * widthFactor,
        maxWidths.xl
      ),
    };
  };

  const gridWidths = calculateGridWidth();

  const sortQueries = (queries) => {
    const statusPriority = {
      "In Progress": 1,
      Processingmake: 2,
      Open: 3,
      Resolved: 4,
    };
    return [...queries].sort(
      (a, b) => statusPriority[a.status] - statusPriority[b.status]
    );
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const validateEmail = (email) => {
    // Basic email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!email || email.trim() === "") {
      return { isValid: false, message: "Email is required" };
    }
    
    if (email.length > 254) {
      return { isValid: false, message: "Email is too long (maximum 254 characters)" };
    }
    
    if (!emailRegex.test(email)) {
      return { isValid: false, message: "Please enter a valid email address" };
    }
    
    // Check for common invalid patterns
    if (email.startsWith('.') || email.endsWith('.') || email.includes('..')) {
      return { isValid: false, message: "Email contains invalid characters" };
    }
    
    if (email.includes(' ')) {
      return { isValid: false, message: "Email cannot contain spaces" };
    }
    
    // Check for valid domain
    const domain = email.split('@')[1];
    if (!domain || domain.length < 2) {
      return { isValid: false, message: "Please enter a valid email address" };
    }
    
    // Check for common disposable email domains (optional - you can customize this list)
    const disposableDomains = [
      'tempmail.org', 'guerrillamail.com', '10minutemail.com', 
      'mailinator.com', 'yopmail.com', 'temp-mail.org'
    ];
    
    if (disposableDomains.some(disposable => domain.toLowerCase().includes(disposable))) {
      return { isValid: false, message: "Please use a valid email address" };
    }
    
    return { isValid: true, message: "" };
  };

  const handleGetOTP = async (e) => {
    e.preventDefault();
    const validation = validateEmail(emailInfo.email);
    if (validation.isValid) {
      setEmailError(false);
      setEmailErrorMessage("");
      setFormLoading(true);
      setOtpRequestError(""); // clear previous error
      
      try {
        // Ensure CSRF token is valid before making the request
        const csrfToken = await ensureCsrfToken();

        const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE_HEADER]:
              BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE.GRIEVANCE_EMAIL,
            ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            query: `mutation($input: SendSessionOtpInput!){
              sendSessionOtp(input: $input){
                status
                statusCode
                errorCode
                message
                data{
                  otpKey
                  resendReadyTime
                  source
                  sourceType
                  user
                }
              }
            }`,
            variables: {
              input: {
                email: emailInfo.email,
                sendCommunication: true,
              },
            },
          }),
        });
        extractAndStoreCsrfToken(response);
        let json = null;
        try {
          json = await response.json();
        } catch {
          json = null;
        }
        const data = json?.data?.sendSessionOtp;
        const gqlError = json?.errors?.[0];
        if (data?.status === "OK" && data?.statusCode === 200) {
          setOtpKey(data.data.otpKey);
          setOtpState(true);
          setTimer(data?.data?.resendReadyTime || 120);
          setIsResendDisabled(true);
          setOtpRequestError(""); // clear error on success
        } else if (data?.errorCode === 'MAX_OTP_ALLOWED_FOR_DAY_EXCEEDED') {
          setOtpRequestError(data.message || "You have reached the maximum allowed OTP requests for today. Please try again after 24 hours.");
        } else {
          setOtpRequestError(
            extractApiErrorMessage(data, gqlError?.message || "Failed to send OTP. Please try again.")
          );
        }
      } catch (error) {
        setOtpRequestError(
          error instanceof Error ? error.message : "Failed to send OTP. Please try again."
        );
      }
      setFormLoading(false);
    } else {
      setEmailError(true);
      setEmailErrorMessage(validation.message);
    }
  };

  const handleResendOtp = async () => {
    if (resendLoading) return;
    setResendLoading(true);
    setResendSuccess(false);
    setOtpError("");
    try {
      // Ensure CSRF token is valid before making the request
      const csrfToken = await ensureCsrfToken();

      const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE_HEADER]:
            BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE.GRIEVANCE_EMAIL,
          ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          query: `mutation($input: SendSessionOtpInput!){
            sendSessionOtp(input: $input){
              status
              statusCode
              errorCode
              message
              data{
                otpKey
                resendReadyTime
                source
                sourceType
                user
              }
            }
          }`,
          variables: {
            input: {
              email: emailInfo.email,
              // Do NOT send the previous otpKey here. If the prior OTP has
              // expired, reusing its key causes the backend to reject the
              // request silently. Letting the server start a fresh session
              // ensures Resend always works after expiry.
              sendCommunication: true,
            },
          },
        }),
      });
      extractAndStoreCsrfToken(response);
      let json = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }
      const data = json?.data?.sendSessionOtp;
      const gqlError = json?.errors?.[0];
      if (data?.status === "OK" && data?.statusCode === 200) {
        if (data.data?.otpKey) {
          setOtpKey(data.data.otpKey);
        }
        setTimer(data?.data?.resendReadyTime || 120);
        setIsResendDisabled(true);
        setOtpError("");
        setOtp(Array(OTP_LENGTH).fill(""));
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 4000);
      } else {
        setOtpError(
          extractApiErrorMessage(data, gqlError?.message || "Failed to resend OTP. Please try again.")
        );
      }
    } catch (error) {
      setOtpError(
        error instanceof Error ? error.message : "Failed to resend OTP. Please try again."
      );
    }
    setResendLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (otp.join("").length === OTP_LENGTH) {
      setIsVerifying(true);
      setOtpError("");
      try {
        // Ensure CSRF token is valid before making the request
        const csrfToken = await ensureCsrfToken();

        const response = await apiFetch(GRAPHQL_ENDPOINTS.GRAPHQL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { [csrfTokenKey]: csrfToken } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            query: `mutation VerifySessionOtp($input: VerifySessionOtpInput!) {
              verifySessionOtp(input: $input) {
                status
                statusCode
                message
                errorCode
                data {
                  accessToken
                }
              }
            }`,
            variables: {
              input: {
                email: emailInfo.email,
                otpKey: otpKey,
                otp: otp.join(""),
              },
            },
          }),
        });
        let json;
        try {
          json = await response.json();
        } catch (jsonErr) {
          json = null;
        }
        const data = json?.data?.verifySessionOtp;
        const gqlError = json?.errors?.[0];
        if (response.ok && data && data.status === "OK" && data.statusCode === 200) {
          const accessToken = data.data.accessToken;
          localStorage.setItem('accessToken', accessToken);
          // Extract CSRF token from response headers and store in sessionStorage
          const csrfToken = extractAndStoreCsrfToken(response);
          
          // Clear preventAutoLogin flag since user is now actively logging in
          localStorage.removeItem('preventAutoLogin');
          sessionStorage.removeItem('preventAutoLogin');
          
          // Force immediate authentication state update
          setIsAuthenticated(true);
          setOtpVerified(true);
          setOtpVerificationStatus('success');
          setOtpError("");
          
          // Reset the change flags since user is now authenticated
          changeClickedRef.current = false;
          changingEmailRef.current = false;
          
          // Clear bypass auth state since we're now properly authenticated
          navigate("/check-query", { 
              state: {}, 
              replace: true 
            });
          
          fetchTicketDetails();
        } else {
          setOtpError(
            extractApiErrorMessage(data, gqlError?.message) ||
            `Error: ${response.status} - OTP verification failed.`
          );
        }
      } catch (error) {
        setOtpError("Failed to verify OTP. Please try again later.");
      }
      setIsVerifying(false);
    } else {
      setOtpError("Invalid OTP. Please enter a 6-digit OTP.");
    }
  };

  const handleEscalate = (ticketId) => {
    setEscalate(true);
    setEscalateTicketDetails(null); // Or pass ticket info if you want
  };

  const handleEscalationGoBack = () => {
    // Reset escalation state to go back to query list
    setEscalate(false);
    setEscalateTicketDetails(null);
    
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken && !isAuthenticated) {
      setIsAuthenticated(true);
    }
  };

  const handleRadioChange = (e) => {
    setPaymentType(e.target.value);
  };

  const handleInputChange = (e) => {
    setInputField(e.target.value);
  };

  const handleCaptchaChange = () => {
    setIsCaptchaChecked(!isCaptchaChecked);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (escalate && escalateTicketDetails) {
      // Validate escalation form
      if (!escalationReason.trim()) {
        setError("Please select a reason for escalation");
        return;
      }
      if (!escalationComment.trim()) {
        setError("Please provide a description of your complaint");
        return;
      }
      
      // Call escalation API
      escalateTicket(escalateTicketDetails.ticketId, escalationReason, escalationComment);
    } else {
      // Original form submission logic
    console.log("Form submitted with: ", {
      paymentType,
      inputField,
      isCaptchaChecked,
    });
    setShowSuccessModal(true);
    }
  };

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError(""); // Clear error on change

    if (value && index < otp.length - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleBackspace = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  /** Enter on any OTP cell submits verification (same as Verify). */
  const handleOtpEnterKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleVerifyOTP();
  };

  const handleOtpDigitKeyDown = (index, e) => {
    handleBackspace(index, e);
    handleOtpEnterKeyDown(e);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!new RegExp(`^\\d{${OTP_LENGTH}}$`).test(pastedData)) return;
    setOtp(pastedData.split(""));
    otpRefs.current[OTP_LENGTH - 1].focus();
  };

  const handleChangeEmail = () => {
    console.log('=== handleChangeEmail FUNCTION CALLED ===');
    console.log('CheckYourQuery: handleChangeEmail called - redirecting to login');
    
    // Debug: Check what's in localStorage before clearing
    console.log('CheckYourQuery: Before clearAuthState - localStorage:', {
      accessToken: localStorage.getItem('accessToken'),
      preventAutoLogin: localStorage.getItem('preventAutoLogin')
    });
    
    // Use the AuthContext clearAuthState function to properly clear all auth data
    clearAuthState();
    
    // Manual backup clearing to ensure tokens are removed
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('accessToken');
    
    // Debug: Check what's in localStorage after clearing
    console.log('CheckYourQuery: After clearAuthState - localStorage:', {
      accessToken: localStorage.getItem('accessToken'),
      preventAutoLogin: localStorage.getItem('preventAutoLogin')
    });
    
    // Set preventAutoLogin flag to prevent autofetch from logging user back in
    localStorage.setItem('preventAutoLogin', 'true');
    sessionStorage.setItem('preventAutoLogin', 'true');
    
    // Debug: Check final localStorage state
    console.log('CheckYourQuery: Final localStorage state:', {
      accessToken: localStorage.getItem('accessToken'),
      preventAutoLogin: localStorage.getItem('preventAutoLogin')
    });
    
    // Set the flag to prevent auto-fetching
    changeClickedRef.current = true;
    changingEmailRef.current = true;
    // Prevent the session-restore effect from re-running on the next render
    // (would otherwise fire briefly between state resets and route replace).
    sessionRestoredRef.current = true;

    // Reset all component state (same as handleGoBack)
    setEmailInfo({ email: "", submitted: false });
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpState(false);
    setOtpVerified(null);
    setOtpVerificationStatus(null);
    setEscalate(false);
    setEscalateTicketDetails(null);
    setEscalateLoading(false);
    setEscalationReason("");
    setEscalationComment("");
    setEscalationSubmitting(false);
    setEscalationSuccessMessage("");
    setTimer(120);
    setIsResendDisabled(true);
    setInputField("");
    setPaymentType("mobile");
    setIsCaptchaChecked(false);
    setEmailTouched(false);
    setEmailError(false);
    setEmailErrorMessage("");
    setOtpRequestError("");
    setOtpError("");
    setTicketQueries([]);
    setLoading(false);
    setError(null);
    setTicketPage(1);
    setTicketTotalPages(1);
    setTicketTotalCount(0);

    // Stay on /check-query (email-entry view). Re-assert bypassAuth so
    // ProtectedRoute keeps rendering CheckYourQuery after clearAuthState()
    // flips isAuthenticated to false — otherwise it would redirect to '/'
    // (Track Payment) because the previous OTP-success navigate had
    // replaced the route state and dropped the original bypassAuth flag.
    navigate('/check-query', { state: { bypassAuth: true }, replace: true });
  };

  const handleEmailChange = (e) => {
    const email = e.target.value.toLowerCase();
    setEmailInfo({ ...emailInfo, email });
    setOtpRequestError(""); // clear error on email change
    if (emailTouched) {
      const validation = validateEmail(email);
      setEmailError(!validation.isValid);
      setEmailErrorMessage(validation.message);
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    const validation = validateEmail(emailInfo.email);
    setEmailError(!validation.isValid);
    setEmailErrorMessage(validation.message);
  };

  const handleEmailFocus = () => {
    setEmailError(false);
    setEmailErrorMessage("");
  };

  const handleGoBack = () => {
    // Back from the ticket list should return to the previous Track Query step
    // (email entry) — NOT pop browser history, which would land the user on
    // CustomerForm (Track Payments) and trigger its auto-redirect to the
    // phone-number transaction history via the still-present grievance token.

    // Clear grievance session auth so:
    //  - the session-restore effect cannot immediately push us back to the
    //    ticket list, and
    //  - if the user later navigates to '/', CustomerForm's auto-login effect
    //    won't fire (guarded by preventAutoLogin in AuthContext).
    clearAuthState();
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('accessToken');
    localStorage.setItem('preventAutoLogin', 'true');
    sessionStorage.setItem('preventAutoLogin', 'true');

    // Prevent the session-restore effect from re-running on the next render.
    sessionRestoredRef.current = true;
    changingEmailRef.current = true;

    // Reset all component state back to the email-entry step.
    setEmailInfo({ email: "", submitted: false });
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpState(false);
    setOtpVerified(null);
    setOtpVerificationStatus(null);
    setEscalate(false);
    setEscalateTicketDetails(null);
    setEscalateLoading(false);
    setEscalationReason("");
    setEscalationComment("");
    setEscalationSubmitting(false);
    setEscalationSuccessMessage("");
    setTimer(120);
    setIsResendDisabled(true);
    setInputField("");
    setPaymentType("mobile");
    setIsCaptchaChecked(false);
    setEmailTouched(false);
    setEmailError(false);
    setEmailErrorMessage("");
    setOtpRequestError("");
    setOtpError("");
    setTicketQueries([]);
    setLoading(false);
    setError(null);
    setTicketPage(1);
    setTicketTotalPages(1);
    setTicketTotalCount(0);

    // Stay on /check-query (email-entry view) and re-assert bypassAuth so
    // ProtectedRoute keeps rendering CheckYourQuery after auth was cleared.
    navigate('/check-query', { state: { bypassAuth: true }, replace: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setOtpState(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpError("");
    setOtpRequestError("");
    setOtpVerified(null);
    setOtpVerificationStatus(null);
    setEmailInfo({ email: "", submitted: false });
    setTicketQueries([]);
  };

  const handlePageClick = (pageNum) => {
    setTicketPage(pageNum);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleVerifyOTP();
  };

  const sortedQueries = sortQueries(ticketQueries);
  const queriesToShow =
    isMobile && !showAllCards ? sortedQueries.slice(0, 3) : sortedQueries;

  // Status color palette mapping
  const statusPalette = {
    OPEN: {
      backgroundColor: '#FFBF0033',
      color: '#FFBF00',
    },
    IN_PROGRESS: {
      backgroundColor: '#0065FF33',
      color: '#0065FF',
    },
    RESOLVED: {
      backgroundColor: '#36B37E33',
      color: '#00875A',
    },
    PROCESSING: {
      backgroundColor: '#E6FCFF',
      color: '#00A3BF',
    },
    default: {
      backgroundColor: '#F0F0F0',
      color: '#000000',
    },
  };

  // Modal state for viewing full description
  const [viewDescriptionId, setViewDescriptionId] = useState(null);
  const [viewDescriptionText, setViewDescriptionText] = useState("");

  // Pagination controls for ticketDetails
  const handleTicketPageClick = (pageNum) => {
    if (pageNum !== ticketPage && pageNum > 0 && pageNum <= ticketTotalPages) {
      setTicketPage(pageNum);
    }
  };

  // Add a helper function for formatting
  function formatDateTime(dateObj) {
    if (!dateObj) return "N/A";
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const min = String(dateObj.getMinutes()).padStart(2, '0');
    const ss = String(dateObj.getSeconds()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh",
      }}
    >
      <Nav />

      {/* Show loading while checking authentication */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Box
          sx={{
            px: { xs: '2px', sm: '40px', md: '70px' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: { xs: '100vw', sm: '100%' },
            width: '100%',
            minHeight: 'calc(100vh - 96px)',
            pb: '8vh',
          }}
        >
        <Grid
          container
          spacing={4}
              sx={{
            mt: (otpVerified === null && !escalate) ? 0 : "55px",
            mb: "24px",
            maxWidth: "1280px",
            width: "100%",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <Grid item xs={12} {...gridWidths} sx={{ minWidth: 0, maxWidth: '100%' }}>
            {!otpState && otpVerified === null && !escalate ? (
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 2, sm: 3 },
                maxWidth: { xs: '100%', sm: '95vw', md: '600px' },
                width: '100%',
                margin: 'auto',
                borderRadius: { xs: '12px', sm: '20px' },
                boxShadow: 'none',
              }}
            >
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: { xs: 500, md: 700 },
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    lineHeight: { xs: '20px', sm: '24px', md: '30px' },
                    color: '#253858',
                  }}
                >
                  Enter the email ID you used to raise the query.
                </Typography>
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={emailInfo.email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  onFocus={handleEmailFocus}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleGetOTP(e); }}
                  error={emailError}
                  helperText={emailErrorMessage}
                  sx={{ mb: 2, fontSize: { xs: '15px', sm: '16px' } }}
                  inputProps={{
                    style: {
                      fontSize: '16px',
                      color: '#253858',
                    }
                  }}
                />
                {otpRequestError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{otpRequestError}</Alert>
                )}
                <Button
                  type="submit"
                  onClick={handleGetOTP}
                  disabled={!validateEmail(emailInfo.email).isValid || formLoading}
                  sx={{
                    py: { xs: 1, sm: 1.5 },
                    mb: { xs: 1.5, sm: 2 },
                    textTransform: 'capitalize',
                    width: '100%',
                    height: { xs: '40px', sm: '48px' },
                    borderRadius: { xs: '8px', sm: '12px' },
                    backgroundColor: '#3965FB',
                    color: '#FFFFFF',
                    fontSize: { xs: '15px', sm: '16px' },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                      color: "#666666",
                    },
                  }}
                >
                  {formLoading ? "Sending OTP..." : "Get OTP"}
                </Button>
                <Typography
                  align="left"
                  sx={{
                    color: "#25385899",
                    fontSize: "14px",
                    lineHeight: "20px",
                    marginTop: "20px",
                    textAlign: { xs: "left", sm: "center" },
                  }}
                >
                  Waiting to get back deducted money?{' '}
                  <Box sx={{ display: { xs: "block", sm: "inline" } }}>
                    <Link
                      to="/"
                      style={{
                        color: "#0065FF",
                        fontSize: "14px",
                        fontWeight: "500",
                        lineHeight: "20px",
                        letterSpacing: "0.0014em",
                        textDecoration: "underline",
                      }}
                    >
                      Track Payments
                    </Link>
                  </Box>
                </Typography>
            </Paper>
            ) : otpState && otpVerified === null && !escalate ? (
              <Box
                sx={{
                  maxWidth: { xs: '100%', sm: '460px' },
                  width: '100%',
                  margin: 'auto',
                }}
              >
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 1.5, sm: 3 },
                maxWidth: { xs: '100%', sm: '460px' },
                width: '100%',
                minWidth: 0,
                margin: 'auto',
                borderRadius: { xs: '12px', sm: '20px' },
                boxShadow: 'none',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {/* Top-left back button */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IconButton onClick={handleChangeEmail} sx={{ p: 0, mr: 1 }} aria-label="Go back">
                  <img src="/Icons/Goback.svg" alt="Go back" />
                </IconButton>
              </Box>
              {/* Heading with blue underline */}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '20px', sm: '22px' },
                  color: "#253858",
                  pb: 1,
                  mb: 2,
                  borderBottom: '3px solid #556CD6',
                  display: 'inline-block',
                }}
              >
                6 digit OTP sent to your email
              </Typography>

              {/* Subtext + Change */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 400, fontSize: '14px', color: '#6B7280' }}>
                  Enter OTP sent on <strong style={{ color: '#253858' }}>{emailInfo.email}</strong>
                </Typography>
                <Button
                  variant="text"
                  onClick={handleChangeEmail}
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#556CD6',
                    flexShrink: 0,
                    ml: 1,
                    '&:hover': { background: 'none', textDecoration: 'underline' },
                  }}
                >
                  Change
                </Button>
              </Box>

              {/* OTP boxes — dynamically sized */}
              <Box
                ref={otpContainerRef}
                sx={{ display: 'flex', gap: 0, justifyContent: 'space-between', my: 4 }}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    
                    inputProps={{
                      style: {
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: 700,
                        borderRadius: "8px",
                        color: "#253858",
                        border: "1px solid #5E6C84",
                        width: "100%",
                      },
                      maxLength: 1,
                    }}
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpDigitKeyDown(index, e)}
                    onPaste={handlePaste}
                    ref={(el) => (otpRefs.current[index] = el)}
                    style={{
                      width: otpIptDimension.width || 48,
                      height: otpIptDimension.height || 56,
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      fontSize: '20px',
                      fontWeight: 500,
                      textAlign: 'center',
                      color: '#1A1F36',
                      background: '#FFFFFF',
                      outline: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#556CD6';
                      e.target.style.boxShadow = '0 0 0 2px rgba(85,108,214,0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#374151';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                ))}
              </Box>

              {otpError && (
                <Typography sx={{ color: 'red', mb: 1.5, textAlign: 'center', fontWeight: 500, fontSize: '14px' }}>
                  {otpError}
                </Typography>
              )}

              {/* Verify button */}
              <Button
                onClick={handleVerifyOTP}
                onKeyDown={handleKeyPress}
                disabled={otp.join('').length !== OTP_LENGTH || isVerifying}
                fullWidth
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  backgroundColor: '#3965FB',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  textTransform: 'capitalize',
                  '&:hover': { backgroundColor: '#335ecc' },
                  '&:disabled': { backgroundColor: '#cccccc', color: '#666666' },
                }}
              >
                {isVerifying ? 'Verifying...' : 'Verify & Continue'}
              </Button>

              {/* Resend section — below button */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {timer > 0 ? (
                  <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                    Resend code in <strong>{formatTime(timer)}</strong>
                  </Typography>
                ) : (
                  <Button
                    onClick={handleResendOtp}
                    disabled={resendLoading}
                    variant="text"
                    sx={{
                      fontSize: '14px',
                      color: '#556CD6',
                      textDecoration: 'underline',
                      textTransform: 'none',
                      p: 0,
                      '&:hover': { background: 'none', color: '#3a4faa' },
                      '&:disabled': { color: '#aaa' },
                    }}
                  >
                    {resendLoading ? 'Sending...' : 'Resend OTP'}
                  </Button>
                )}
              </Box>

              <Box sx={{ borderTop: '1px solid #E5E7EB', pt: 2, textAlign: 'center' }}>
                <Typography sx={{ color: '#25385899', fontSize: '14px' }}>
                  Waiting to get back deducted money?{' '}
                  <Link to="/" style={{ color: '#0065FF', fontWeight: 500, textDecoration: 'underline' }}>
                    Track Payments
                  </Link>
                </Typography>
              </Box>
            </Paper>
              </Box>
          ) : otpVerified ? (
            !escalate ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <IconButton onClick={handleGoBack} sx={{ p: 0, mr: 1 }}>
                      <img src="/Icons/Goback.svg" alt="Go back" />
                    </IconButton>
                  </Box>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  maxWidth: { xs: '100%', sm: '95vw', md: '1200px' },
                  width: '100%',
                  margin: 'auto',
                  borderRadius: { xs: '12px', sm: '20px' },
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: { xs: 2, sm: 2.4 },
                    backgroundColor: '#EBF3FF',
                    padding: { xs: '8px 8px', sm: '8px 20px' },
                    borderRadius: { xs: '8px', sm: '12px' },
                    gap: { xs: 1, sm: 0 },
                  }}
                >
                  <Typography variant="body1">
                    Your email ID is <strong>{emailInfo.email}.</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      onClick={handleLogout}
                      sx={{
                        height: 36,
                        px: 2.5,
                        borderRadius: '38px',
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '24px',
                        letterSpacing: '0.0016em',
                        borderColor: '#B3BAC5',
                        color: '#253858',
                        backgroundColor: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#0065FF',
                          backgroundColor: '#F9FAFB',
                          color: '#253858',
                        },
                      }}
                    >
                      Log out
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 700, fontSize: { xs: '16px', sm: '18px' } }}
                >
                  Recent Queries
                </Typography>

                    {/* Loading State */}
                    {loading && (
                      <Box sx={{ mb: 3, textAlign: "center" }}>
                        <Typography sx={{ color: "#5E6C84", mb: 2 }}>
                          Loading your ticket details...
                  </Typography>
                        <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
                      </Box>
                    )}

                    {/* Error State */}
                    {error && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    )}

                    {/* No Tickets Found State */}
                    {!loading && !error && ticketQueries.length === 0 && (
                      <Box sx={{ mb: 3, textAlign: "center", py: 4 }}>
                        <Typography sx={{ color: "#5E6C84", mb: 2, fontSize: "16px" }}>
                          No tickets found for this email address.
                  </Typography>
                        <Typography sx={{ color: "#5E6C84", fontSize: "14px" }}>
                          If you believe this is an error, please contact support.
                        </Typography>
                      </Box>
                    )}

                    {/* Original Query Cards */}
                    {isMobile ? (
                      <>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 2,
                            width: '100%',
                            minHeight: { xs: '200px', sm: '400px' },
                          }}
                        >
                          {sortQueries(ticketQueries).map((query) => (
                            <Card
                              key={query.id}
                              sx={{
                                width: '100%',
                                borderRadius: { xs: '8px', sm: '12px' },
                                boxShadow: 'none',
                                backgroundColor: '#F9FAFB',
                                position: 'relative',
                                border: '1px dashed rgba(1, 9, 17, 0.55)',
                                transition:
                                  'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              <CardContent>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                  <Typography
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontSize: { xs: '15px', sm: '16px' },
                                      fontWeight: 400,
                                      lineHeight: { xs: '20px', sm: '24px' },
                                      color: '#253858',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Ticket ID #{query.id}
                                  </Typography>
                                  <Chip
                                    label={query.status?.replace(/_/g, ' ')}
                                    sx={{
                                      height: "22px",
                                      flexShrink: 0,
                                      width: 'auto',
                                      borderRadius: "100px",
                                      backgroundColor: statusPalette[query.status?.toUpperCase()]?.backgroundColor || statusPalette.default.backgroundColor,
                                      color: statusPalette[query.status?.toUpperCase()]?.color || statusPalette.default.color,
                                      fontWeight: 700,
                                      fontSize: "12px",
                                      letterSpacing: "0.0016em",
                                      '& .MuiChip-label': {
                                        overflow: 'visible',
                                        whiteSpace: 'nowrap',
                                        px: 1.25,
                                      },
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mt: 1 }}>
                                  <Typography
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "#253858",
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      minHeight: '40px',
                                    }}
                                  >
                                    {query.description}
                                  </Typography>
                                  <Button
                                    size="small"
                                    sx={{
                                      minWidth: 0,
                                      flexShrink: 0,
                                      fontSize: { xs: '11px', sm: '12px' },
                                      textTransform: 'capitalize',
                                      p: 0,
                                      alignSelf: 'center',
                                    }}
                                    onClick={() => {
                                      setViewDescriptionId(query.id);
                                      setViewDescriptionText(query.description);
                                    }}
                                  >
                                    View
                                  </Button>
                                </Box>
                              </CardContent>
                              <Box
                                sx={{
                                  backgroundColor: "#EDEFF2",
                                  borderTop: "1px dashed #DFE1E6",
                                  p: { xs: 1, sm: 1.5 },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: { xs: '13px', sm: '14px' },
                                    lineHeight: { xs: '18px', sm: '20px' },
                                    color: '#253858',
                                  }}
                                >
                                  Transaction • {query.transactionId}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: { xs: '12px', sm: '14px' },
                                    lineHeight: { xs: '16px', sm: '20px' },
                                    color: '#253858',
                                    opacity: 0.6,
                                  }}
                                >
                                  {query.createdOn ? formatDateTime(new Date(Date.parse(query.createdOn.replace(/-/g, '/')) || query.createdOn)) : 'N/A'}
                                </Typography>
                              </Box>
                              {query.status?.toUpperCase() !== 'RESOLVED' && (
                                <Typography
                                  sx={{
                                    p: { xs: 1, sm: 1.5 },
                                    cursor: 'pointer',
                                    color: '#25385899',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    lineHeight: { xs: '18px', sm: '20px' },
                                  }}
                                  onClick={() => handleEscalate(query.id)}
                                >
                                  Awaiting quick resolution?{" "}
                                  <span
                                    style={{
                                      fontWeight: "500",
                                      color: "#0065FF",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Escalate this query
                                  </span>
                                </Typography>
                              )}
                            </Card>
                          ))}
                        </Box>
                        {/* Pagination: First / Prev / Next / Last */}
                        {ticketTotalPages > 1 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              mt: 3,
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Button
                              disabled={ticketPage === 1}
                              onClick={() => handleTicketPageClick(1)}
                              aria-label="First page"
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 44,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              {'|<'}
                            </Button>
                            <Button
                              disabled={ticketPage === 1}
                              onClick={() => handleTicketPageClick(ticketPage - 1)}
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 60,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              Prev
                            </Button>
                            <Button
                              disabled={ticketPage >= ticketTotalPages}
                              onClick={() => handleTicketPageClick(ticketPage + 1)}
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 60,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              Next
                            </Button>
                            <Button
                              disabled={ticketPage >= ticketTotalPages}
                              onClick={() => handleTicketPageClick(ticketTotalPages)}
                              aria-label="Last page"
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 44,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              {'>|'}
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(3, 1fr)",
                            },
                            gap: 3,
                          }}
                        >
                          {sortQueries(ticketQueries).map((query) => (
                              <Card
                                key={query.id}
                                sx={{
                                  width: "100%",
                                  borderRadius: "20px",
                                  boxShadow: "none",
                                  height: "auto",
                                  backgroundColor: "#F9FAFB",
                                  position: "relative",
                                  border: "1px dashed rgba(1, 9, 17, 0.55)",
                                  transition:
                                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    backgroundColor: "#FFFFFF",
                                  },
                                }}
                              >
                                <CardContent>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                    <Typography
                                      sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "16px",
                                        fontWeight: 400,
                                        lineHeight: "24px",
                                        color: "#253858",
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Ticket ID #{query.id}
                                    </Typography>
                                    <Chip
                                      label={query.status?.replace(/_/g, ' ')}
                                      sx={{
                                        height: "22px",
                                        flexShrink: 0,
                                        width: 'auto',
                                        borderRadius: "100px",
                                        backgroundColor: statusPalette[query.status?.toUpperCase()]?.backgroundColor || statusPalette.default.backgroundColor,
                                        color: statusPalette[query.status?.toUpperCase()]?.color || statusPalette.default.color,
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        letterSpacing: "0.0016em",
                                        '& .MuiChip-label': {
                                          overflow: 'visible',
                                          whiteSpace: 'nowrap',
                                          px: 1.25,
                                        },
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mt: 1 }}>
                                    <Typography
                                      sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        color: "#253858",
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        minHeight: '40px',
                                      }}
                                    >
                                      {query.description}
                                    </Typography>
                                    <Button
                                      size="small"
                                      sx={{
                                        minWidth: 0,
                                        flexShrink: 0,
                                        fontSize: '12px',
                                        textTransform: 'capitalize',
                                        p: 0,
                                        alignSelf: 'center',
                                      }}
                                      onClick={() => {
                                        setViewDescriptionId(query.id);
                                        setViewDescriptionText(query.description);
                                      }}
                                    >
                                      View
                                    </Button>
                                  </Box>
                                </CardContent>
                                <Box
                                  sx={{
                                    backgroundColor: "#EDEFF2",
                                    borderTop: "1px dashed #DFE1E6",
                                    p: 1.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "14px",
                                      lineHeight: "20px",
                                      color: "#253858",
                                    }}
                                  >
                                    Transaction • {query.transactionId}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: "14px",
                                      lineHeight: "20px",
                                      color: "#253858",
                                      opacity: 0.6,
                                    }}
                                  >
                                    {query.createdOn ? formatDateTime(new Date(Date.parse(query.createdOn.replace(/-/g, '/')) || query.createdOn)) : 'N/A'}
                                  </Typography>
                                </Box>
                                {query.status?.toUpperCase() !== 'RESOLVED' && (
                                  <Typography
                                    sx={{
                                      p: 1.5,
                                      cursor: "pointer",
                                      color: "#25385899",
                                      fontSize: "14px",
                                      lineHeight: "20px",
                                    }}
                                    onClick={() => handleEscalate(query.id)}
                                  >
                                    Awaiting quick resolution?{" "}
                                    <span
                                      style={{
                                        fontWeight: "500",
                                        color: "#0065FF",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Escalate this query
                                    </span>
                                  </Typography>
                                )}
                              </Card>
                            ))}
                        </Box>
                        {/* Pagination: First / Prev / Next / Last */}
                        {ticketTotalPages > 1 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              mt: 3,
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Button
                              disabled={ticketPage === 1}
                              onClick={() => handleTicketPageClick(1)}
                              aria-label="First page"
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 44,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              {'|<'}
                            </Button>
                            <Button
                              disabled={ticketPage === 1}
                              onClick={() => handleTicketPageClick(ticketPage - 1)}
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 60,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              Prev
                            </Button>
                            <Button
                              disabled={ticketPage >= ticketTotalPages}
                              onClick={() => handleTicketPageClick(ticketPage + 1)}
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 60,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              Next
                            </Button>
                            <Button
                              disabled={ticketPage >= ticketTotalPages}
                              onClick={() => handleTicketPageClick(ticketTotalPages)}
                              aria-label="Last page"
                              sx={{
                                backgroundColor: '#FFFFFF',
                                color: '#0065FF',
                                minWidth: 44,
                                height: 32,
                                fontSize: '12px',
                                lineHeight: '14.06px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                textTransform: 'capitalize',
                                '&.Mui-disabled': { color: '#A5ADBA', backgroundColor: '#F4F5F7' },
                              }}
                            >
                              {'>|'}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                </>
            ) : (
              <EscalationInfo ticketDetails={escalateTicketDetails} onGoBack={handleEscalationGoBack} />
            )
            ) : null}
        </Grid>
      </Grid>
      </Box>
      )}

      {/* Description Modal */}
      <Dialog 
        open={!!viewDescriptionId} 
        onClose={() => setViewDescriptionId(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: '95vw', sm: '500px' },
            maxHeight: { xs: '90vh', sm: '80vh' },
            p: { xs: 1, sm: 3 },
            borderRadius: { xs: 2, sm: 3 },
            position: 'relative',
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setViewDescriptionId(null)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#253858',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ overflowX: 'hidden', px: { xs: 1.5, sm: 3 }, pt: { xs: 4, sm: 3 } }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '18px' }, mb: 2 }}>Ticket Description</Typography>
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              fontSize: { xs: '14px', sm: '16px' },
              color: '#253858',
              mb: 2,
              maxWidth: '100%',
            }}
          >
            {viewDescriptionText}
          </Typography>
        </DialogContent>
      </Dialog>

      <FAQSection />
      <Snackbar
        open={resendSuccess}
        autoHideDuration={4000}
        onClose={() => setResendSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setResendSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          A new OTP has been sent to your email.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckYourQuery;